import React from 'react';
import { Link } from 'react-router-dom';

const FEATURES = [
  {
    title: 'Roommate Matching',
    desc: 'A compatibility quiz pairs you with the right people based on lifestyle, schedule, and budget.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
    color: { bg: '#FDF8E8', text: '#C8A800' },
  },
  {
    title: 'Apartment Listings',
    desc: 'Browse verified rentals near campus. Filter by price, bedrooms, and pet policy.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        <polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    ),
    color: { bg: '#EFF6FF', text: '#3B82F6' },
  },
  {
    title: 'Sublease Board',
    desc: 'Find or post subleases for any semester. Ideal for study abroad and internships.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
      </svg>
    ),
    color: { bg: '#FAF5FF', text: '#9333EA' },
  },
  {
    title: 'Rent Payments',
    desc: 'Pay rent directly through the app via Stripe. Landlords receive payouts automatically.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
        <rect x="1" y="4" width="22" height="16" rx="2"/>
        <line x1="1" y1="10" x2="23" y2="10"/>
      </svg>
    ),
    color: { bg: '#F0FDF4', text: '#16A34A' },
  },
];

const HOW_IT_WORKS = [
  { step: '01', title: 'Create your profile', desc: 'Sign up as a student or landlord in under a minute.' },
  { step: '02', title: 'Find your match', desc: 'Browse listings, take the roommate quiz, or post your place.' },
  { step: '03', title: 'Move in & pay rent', desc: 'Connect with roommates and pay rent securely through the app.' },
];

function MockCard({ title, price, beds, distance, img }) {
  return (
    <div className="bg-white rounded-xl border border-[#EBEBEB] p-4 shadow-sm">
      <img src={img} alt={title} className="w-full h-24 rounded-lg object-cover mb-3" />
      <p className="font-semibold text-[#1B1B1B] text-sm">{title}</p>
      <p className="text-xs text-[#999999] nav-link mb-2">{distance} from Pentacrest</p>
      <div className="flex items-center justify-between">
        <span className="text-[#C8A800] font-bold text-sm">${price}<span className="text-[#AAAAAA] font-normal">/mo</span></span>
        <span className="text-xs text-[#AAAAAA] nav-link">{beds} bed</span>
      </div>
    </div>
  );
}

function MockMatch({ name, pct, tags }) {
  return (
    <div className="bg-white rounded-xl border border-[#EBEBEB] p-3.5 shadow-sm flex items-center gap-3">
      <div className="w-9 h-9 rounded-full bg-[#FDF8E8] flex items-center justify-center flex-shrink-0 text-sm font-bold text-[#C8A800]">
        {name[0]}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-[#1B1B1B] text-sm">{name}</p>
        <div className="flex gap-1 mt-1 flex-wrap">
          {tags.map(t => (
            <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#F4F4F4] text-[#777777] nav-link">{t}</span>
          ))}
        </div>
      </div>
      <div className="flex-shrink-0 text-right">
        <p className="text-[#C8A800] font-bold text-sm">{pct}%</p>
        <p className="text-[10px] text-[#AAAAAA] nav-link">match</p>
      </div>
    </div>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white flex flex-col">

      {/* ── Header ── */}
      <header className="bg-white border-b border-[#E8E8E8] sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-9 h-9 flex-shrink-0">
              <path d="M20 8 C14 8 10 13 10 18 C10 24 14 28 20 30 C26 28 30 24 30 18 C30 13 26 8 20 8Z" fill="#C8A800"/>
              <path d="M20 16 C16 14 10 15 6 12 C8 17 12 20 20 20Z" fill="#B89700"/>
              <path d="M20 16 C24 14 30 15 34 12 C32 17 28 20 20 20Z" fill="#B89700"/>
              <ellipse cx="20" cy="10" rx="4.5" ry="4" fill="#C8A800"/>
              <path d="M22 10 L25.5 11.5 L22 12.5Z" fill="#1B1B1B"/>
              <circle cx="21" cy="9.5" r="1" fill="#1B1B1B"/>
              <circle cx="21.4" cy="9.2" r="0.35" fill="white"/>
              <path d="M17 29 C15 32 13 34 11 35 C14 34 17 32 20 30 C23 32 26 34 29 35 C27 34 25 32 23 29Z" fill="#B89700"/>
              <path d="M17 30 L15 34 M17 30 L17 34.5 M17 30 L19 34" stroke="#1B1B1B" strokeWidth="0.8" strokeLinecap="round"/>
              <path d="M23 30 L21 34 M23 30 L23 34.5 M23 30 L25 34" stroke="#1B1B1B" strokeWidth="0.8" strokeLinecap="round"/>
            </svg>
            <div>
              <span className="text-xl font-bold text-[#1B1B1B] tracking-tight" style={{ fontFamily: 'Georgia, serif' }}>NestHawk</span>
              <span className="hidden sm:inline text-xs text-[#AAAAAA] ml-2 uppercase tracking-widest nav-link">Iowa City</span>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-6 text-sm text-[#555555] nav-link">
            <a href="#features" className="hover:text-[#1B1B1B] transition-colors">Features</a>
            <a href="#how-it-works" className="hover:text-[#1B1B1B] transition-colors">How it works</a>
            <a href="#pricing" className="hover:text-[#1B1B1B] transition-colors">Pricing</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" className="btn-ghost">Sign In</Link>
            <Link to="/signup" className="btn-primary">Get Started</Link>
          </div>
        </div>
      </header>

      <main className="flex-1">

        {/* ── Hero ── */}
        <section className="max-w-6xl mx-auto px-6 lg:px-12 py-16 sm:py-24 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">

            {/* Left: copy */}
            <div>
              <div className="inline-flex items-center gap-2 border border-[#C8A800]/30 rounded-full px-3.5 py-1.5 mb-8" style={{ backgroundColor: 'rgba(200,168,0,0.08)' }}>
                <span className="w-1.5 h-1.5 bg-[#C8A800] rounded-full" />
                <span className="text-xs font-semibold text-[#9A7F00] tracking-wide uppercase nav-link">Built for University of Iowa</span>
              </div>

              <h1 className="text-5xl lg:text-6xl font-bold text-[#1B1B1B] leading-[1.1] tracking-tight mb-6">
                Housing &amp; roommates for{' '}
                <span className="text-[#C8A800]">Hawkeyes.</span>
              </h1>

              <p className="text-lg text-[#666666] leading-relaxed mb-10 nav-link">
                Find compatible roommates, browse apartments near campus, post a sublease, and pay rent — all in one platform made for Iowa City students.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mb-12">
                <Link to="/signup" className="btn-primary px-7 py-3 text-base">
                  Create a Free Account
                </Link>
                <Link to="/signup?type=landlord" className="btn-outline px-7 py-3 text-base">
                  List Your Property
                </Link>
              </div>

              {/* Stats */}
              <div className="flex items-center gap-8">
                <div>
                  <p className="text-2xl font-bold text-[#1B1B1B] tracking-tight">Free</p>
                  <p className="text-xs text-[#999999] mt-0.5 nav-link">to sign up</p>
                </div>
              </div>
            </div>

            {/* Right: UI preview */}
            <div className="hidden lg:block relative">
              <div className="relative z-10 space-y-3">
                {/* Listings preview */}
                <div className="bg-[#FAFAFA] rounded-2xl border border-[#EBEBEB] p-4">
                  <p className="text-xs font-semibold text-[#AAAAAA] uppercase tracking-widest nav-link mb-3">Apartments near campus</p>
                  <div className="grid grid-cols-2 gap-3">
                    <MockCard title="The Hawkeye Flats" price="750" beds="2" distance="0.3 mi" img="https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=400&q=80" />
                    <MockCard title="Clinton St. Studio" price="620" beds="1" distance="0.5 mi" img="https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=400&q=80" />
                  </div>
                </div>

                {/* Roommate matches preview */}
                <div className="bg-[#FAFAFA] rounded-2xl border border-[#EBEBEB] p-4">
                  <p className="text-xs font-semibold text-[#AAAAAA] uppercase tracking-widest nav-link mb-3">Your top roommate matches</p>
                  <div className="space-y-2">
                    <MockMatch name="Jordan M." pct={94} tags={['Early bird', 'Clean', 'Quiet']} />
                    <MockMatch name="Alex T." pct={88} tags={['Night owl', 'Social', 'No pets']} />
                  </div>
                </div>
              </div>

              {/* Decorative background blob */}
              <div className="absolute -top-8 -right-8 w-64 h-64 rounded-full opacity-20 blur-3xl pointer-events-none" style={{ background: 'radial-gradient(circle, #C8A800 0%, transparent 70%)' }} />
            </div>

          </div>
        </section>

        {/* ── Features ── */}
        <section id="features" className="bg-[#FAFAFA] border-y border-[#EBEBEB] py-20">
          <div className="max-w-6xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-14">
              <h2 className="text-3xl font-bold text-[#1B1B1B] mb-3">Everything you need in one place</h2>
              <p className="text-[#777777] nav-link max-w-md mx-auto">From finding roommates to paying rent, NestHawk covers the full student housing experience.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {FEATURES.map(f => (
                <div key={f.title} className="bg-white rounded-xl border border-[#EBEBEB] p-6 hover:border-[#DDDDDD] hover:shadow-sm transition-all duration-200">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: f.color.bg, color: f.color.text }}>
                    {f.icon}
                  </div>
                  <h3 className="font-bold text-[#1B1B1B] mb-2">{f.title}</h3>
                  <p className="text-sm text-[#777777] leading-relaxed nav-link">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── How it works ── */}
        <section id="how-it-works" className="py-20">
          <div className="max-w-6xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-14">
              <h2 className="text-3xl font-bold text-[#1B1B1B] mb-3">How it works</h2>
              <p className="text-[#777777] nav-link">Up and running in three simple steps.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
              {/* Connector line */}
              <div className="hidden md:block absolute top-7 left-[calc(16.6%+1rem)] right-[calc(16.6%+1rem)] h-px bg-[#EBEBEB]" />
              {HOW_IT_WORKS.map(s => (
                <div key={s.step} className="relative text-center">
                  <div className="w-14 h-14 rounded-full bg-[#FDF8E8] border-2 border-[#C8A800]/30 flex items-center justify-center mx-auto mb-5 relative z-10">
                    <span className="font-bold text-[#C8A800] nav-link text-sm">{s.step}</span>
                  </div>
                  <h3 className="font-bold text-[#1B1B1B] mb-2">{s.title}</h3>
                  <p className="text-sm text-[#777777] nav-link leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="py-24">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold text-[#1B1B1B] mb-4">Ready to find your place?</h2>
            <p className="text-[#777777] nav-link mb-8 text-lg">Join students and landlords already using NestHawk in Iowa City.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/signup" className="btn-primary px-8 py-3 text-base">Create an Account</Link>
              <Link to="/login" className="btn-outline px-8 py-3 text-base">Sign In</Link>
            </div>
          </div>
        </section>

      </main>

      {/* ── Footer ── */}
      <footer className="bg-white border-t border-[#EBEBEB] py-8">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 flex-shrink-0">
              <path d="M20 8 C14 8 10 13 10 18 C10 24 14 28 20 30 C26 28 30 24 30 18 C30 13 26 8 20 8Z" fill="#C8A800"/>
              <path d="M20 16 C16 14 10 15 6 12 C8 17 12 20 20 20Z" fill="#B89700"/>
              <path d="M20 16 C24 14 30 15 34 12 C32 17 28 20 20 20Z" fill="#B89700"/>
              <ellipse cx="20" cy="10" rx="4.5" ry="4" fill="#C8A800"/>
              <path d="M22 10 L25.5 11.5 L22 12.5Z" fill="#1B1B1B"/>
              <circle cx="21" cy="9.5" r="1" fill="#1B1B1B"/>
              <circle cx="21.4" cy="9.2" r="0.35" fill="white"/>
              <path d="M17 29 C15 32 13 34 11 35 C14 34 17 32 20 30 C23 32 26 34 29 35 C27 34 25 32 23 29Z" fill="#B89700"/>
            </svg>
            <span className="text-sm font-semibold text-[#1B1B1B]" style={{ fontFamily: 'Georgia, serif' }}>NestHawk</span>
            <span className="text-sm text-[#AAAAAA] nav-link">· © 2025 · Iowa City, IA</span>
          </div>
          <span className="text-sm text-[#BBBBBB] nav-link">Secure payments via Stripe</span>
        </div>
      </footer>

    </div>
  );
}
